import type { ReactNode } from 'react';
import { create } from 'zustand';
import { getErrorMessage } from '@/lib/api/error';

// Every sensitive mutation records why it happened; bounds relaxed from the
// legacy portal's 35–500 (BopActionDialog) per ops feedback.
export const JUSTIFICATION_MIN_LENGTH = 5;
export const JUSTIFICATION_MAX_LENGTH = 1000;

export const isJustificationValid = (justification: string): boolean => {
  const { length } = justification.trim();
  return length >= JUSTIFICATION_MIN_LENGTH && length <= JUSTIFICATION_MAX_LENGTH;
};

export interface ConfirmActionOptions {
  title: string;
  description?: ReactNode;
  actionLabel?: string;
  destructive?: boolean;
  /** `wide` for descriptions that carry a table or comparison */
  size?: 'default' | 'wide';
  /** Audit-justification textarea (5–1000 chars). Default true — mutations are audited. */
  requireJustification?: boolean;
  /** Runs while the dialog shows a loading state; a throw keeps the dialog open with the error. */
  onConfirm?: (justification: string) => Promise<unknown> | unknown;
}

interface ActiveRequest extends ConfirmActionOptions {
  resolve: (justification: string | null) => void;
}

interface ConfirmActionState {
  request: ActiveRequest | null;
  isSubmitting: boolean;
  submitError: string | null;
  open: (request: ActiveRequest) => void;
  cancel: () => void;
  submit: (justification: string) => Promise<void>;
}

export const useConfirmActionStore = create<ConfirmActionState>((set, get) => ({
  request: null,
  isSubmitting: false,
  submitError: null,
  open: (request) => set({ request, isSubmitting: false, submitError: null }),
  cancel: () => {
    const { request, isSubmitting } = get();
    if (!request || isSubmitting) return;
    request.resolve(null);
    set({ request: null, submitError: null });
  },
  submit: async (justification) => {
    const { request, isSubmitting } = get();
    if (!request || isSubmitting) return;
    if (request.onConfirm) {
      set({ isSubmitting: true, submitError: null });
      try {
        await request.onConfirm(justification);
      } catch (error) {
        set({ isSubmitting: false, submitError: getErrorMessage(error) });
        return;
      }
    }
    request.resolve(justification);
    set({ request: null, isSubmitting: false, submitError: null });
  },
}));

/**
 * Imperative confirm — resolves with the justification when confirmed (empty string
 * when `requireJustification: false`), or `null` when cancelled.
 *
 *   const justification = await confirmAction({ title: 'Block user?', destructive: true });
 *   if (justification === null) return;
 */
export const confirmAction = (options: ConfirmActionOptions): Promise<string | null> => new Promise((resolve) => useConfirmActionStore.getState().open({ ...options, resolve }));
